import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, Settings } from 'lucide-react'
import { SALES_LINE_STATE_LABELS, erfuelltRecht, type BillingWorkplaceTab, geldText } from '@symphenia/shared-types'
import { FilterBar } from '@/components/list/FilterBar'
import { ExportButton } from '@/components/ui/ExportButton'
import { useTranslation } from 'react-i18next'
import { HelpPopover } from '@/components/ui/HelpPopover'
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions'
import type { ActiveFilter, FilterFieldDef } from '@/components/list/list.types'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { inlineInput, pageWide } from '@/lib/form-styles'
import { loadUiSetting, markUiSettingApplied, saveUiSetting } from '@/lib/ui-settings'
import { useTerminology, type Terms } from '@/lib/useTerminology'
import { copySalesLines, fetchWorkplace, invoicePaymentPlanItem, type WorkplaceFilter, type WorkplaceRow, workplaceExportRequest } from '../api/project-sales.api'
import { fetchUsers } from '../api/projects-head.api'
import type { LookupUser } from '../types/project.types'
import { BelegDialog } from '../components/ProjectBillingListTab'
import { RegieProposalDialog } from '../components/RegieProposalDialog'
import { alsTag } from '../project-labels'

const schmal = inlineInput.replace('w-full', '')
const zahl = (n: number | null | undefined) => (n === null || n === undefined ? '—' : n.toLocaleString(navigator.language, { maximumFractionDigits: 3 }))
/** Betrag im Geldformat (`geld.ts`); leer → „—". */
const geld = (n: number | null | undefined) => geldText(n) || '—'

const REITER: [BillingWorkplaceTab, string, string][] = [
  ['due', 'Fällig', 'Stichtag erreicht und etwas Abrechenbares da.'],
  ['regie', 'Regie offen', 'Stunden ohne Verkaufszeile, je Position mit Regie-Abrechnung.'],
  ['deliverable', 'Lieferbar', 'Offene Verkaufszeilen — für Lieferschein oder direkte Rechnung.'],
  ['billable', 'Abrechenbar', 'Gelieferte Zeilen ohne Rechnung, Pauschal-Teilrechnungen und fällige Raten.'],
  ['done', 'Abgeschlossen', '„Abrechnung fertig" freigegeben, aber noch nicht abgerechnet.'],
]

// Zahlungsplan ist seit Nachtrag 2 ein Intervall, kein Schema mehr — im Filter nur die drei Schemata.
const SCHEMA: Record<string, string> = { TIME_AND_MATERIAL: 'Regie', LUMP_SUM: 'Pauschal', UNIT_PRICE: 'Einheitspreise' }

// Eine Funktion, keine Konstante: Die Begriffe des Mandanten und die Benutzer stehen erst zur Laufzeit
// fest (terminologie.md). Projekt, Position und Kunde „enthält" wie im Einkauf, die Leitung als Auswahl wie
// in der Projektliste (Auftraggeber 9.10.2026).
const filterFelder = (terms: Terms, benutzer: LookupUser[]): FilterFieldDef[] => [
  { key: 'q', label: 'Suche', type: 'text', category: 'core', operators: ['contains'] },
  { key: 'projectSearch', label: terms.project, type: 'text', category: 'core', operators: ['contains'] },
  { key: 'positionSearch', label: terms.position, type: 'text', category: 'core', operators: ['contains'] },
  { key: 'clientName', label: 'Kunde', type: 'text', category: 'core', operators: ['contains'] },
  {
    key: 'projectLead',
    label: terms.lead,
    type: 'select',
    category: 'core',
    operators: ['is'],
    options: benutzer.map((u) => ({ value: u.id, label: u.name })),
  },
  {
    key: 'scheme',
    label: 'Abrechnung',
    type: 'select',
    category: 'core',
    operators: ['is'],
    options: Object.entries(SCHEMA).map(([value, label]) => ({ value, label })),
  },
]

/** Was die Filterleiste an die Schnittstelle gibt — für die Liste und für ihren Export. */
function alsFilter(werte: Record<string, ActiveFilter>): WorkplaceFilter {
  const f: WorkplaceFilter = {}
  for (const [k, a] of Object.entries(werte)) {
    if (!a.value) continue
    if (k === 'q') f.q = a.value
    if (k === 'scheme') f.scheme = a.value
    if (k === 'projectSearch') f.projectSearch = a.value
    if (k === 'positionSearch') f.positionSearch = a.value
    if (k === 'clientName') f.clientName = a.value
    if (k === 'projectLead') f.projectLeadUserId = a.value
  }
  return f
}

interface Spalte {
  key: string
  label: string
  rechts?: boolean
  preis?: boolean
}

const COLUMN_LAYOUT_KEY = 'columnbar_layout_project_billing'
const STANDARD = ['client', 'project', 'position', 'what', 'quantity', 'state', 'value', 'due']

function ladeSpalten(): string[] {
  const g = loadUiSetting<{ columnKeys?: string[] } | null>(COLUMN_LAYOUT_KEY, null)
  return g?.columnKeys?.length ? g.columnKeys : STANDARD
}

/**
 * AP-35 Detailblatt 7 5. — **Abrechnungsarbeitsplatz** (`/project-billing`).
 *
 * Der Ort der Buchhaltung: was fällig ist, was an Regie offen ist, was geliefert
 * und noch nicht abgerechnet ist — über alle Projekte. **Arbeitsplätze erzeugen
 * Belege aus einer Auswahl** (Entscheidung 26), als Entwurf je Kunde; erst das
 * Festschreiben ändert das Projekt.
 */
export default function ProjectBillingPage() {
  const terms = useTerminology()
  const navigate = useNavigate()
  const { user, hasModule } = useAuthStore()
  const preise = erfuelltRecht('projects.price.view', user?.permissions)
  const belegwesen = hasModule('CORRESPONDENCE')
  const darfExport = erfuelltRecht('projects.export.run', user?.permissions)
  const { t } = useTranslation()

  const ALLE: Spalte[] = [
    { key: 'client', label: 'Kunde' },
    { key: 'project', label: terms.project },
    { key: 'position', label: terms.position },
    { key: 'what', label: 'Was' },
    { key: 'quantity', label: 'Menge / Stunden', rechts: true },
    { key: 'state', label: 'Stand' },
    { key: 'value', label: 'Betrag', rechts: true, preis: true },
    { key: 'due', label: 'Stichtag' },
    { key: 'scheme', label: 'Abrechnung' },
  ]

  const [reiter, setReiter] = useState<BillingWorkplaceTab>('due')
  const [gruppe, setGruppe] = useState<'client' | 'project'>('client')
  const [filterWerte, setFilterWerte] = useState<Record<string, ActiveFilter>>({})
  const [spalten, setSpalten] = useState<string[]>(ladeSpalten)
  const [konfig, setKonfig] = useState(false)
  const [zeilen, setZeilen] = useState<WorkplaceRow[]>([])
  const [markiert, setMarkiert] = useState<Set<string>>(new Set())
  const [erzeugen, setErzeugen] = useState<'DELIVERY_NOTE' | 'INVOICE' | null>(null)
  const [regie, setRegie] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [hinweis, setHinweis] = useState<string | null>(null)
  const [laedt, setLaedt] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Für das Filterfeld der Leitung — getrennt geladen, damit ein Fehler hier die Liste nicht aufhält.
  const [benutzer, setBenutzer] = useState<LookupUser[]>([])
  useEffect(() => {
    fetchUsers().then(setBenutzer).catch(() => setBenutzer([]))
  }, [])
  const felder = useMemo(() => filterFelder(terms, benutzer), [terms, benutzer])

  const laden = useCallback(
    async (werte: Record<string, ActiveFilter>) => {
      setLaedt(true)
      try {
        setZeilen(await fetchWorkplace(reiter, alsFilter(werte)))
        setMarkiert(new Set())
        setFehler(null)
      } catch (e) {
        setFehler(apiErrorText(e, 'Die Liste konnte nicht geladen werden.'))
      } finally {
        setLaedt(false)
      }
    },
    [reiter],
  )

  useEffect(() => {
    void laden(filterWerte)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reiter])

  const gezeigt = spalten.map((k) => ALLE.find((c) => c.key === k)).filter((c): c is Spalte => !!c && (!c.preis || preise))
  const hinzu = ALLE.filter((c) => !spalten.includes(c.key) && (!c.preis || preise))
  const auswahl = [...markiert]

  const gruppen = useMemo(() => {
    const m = new Map<string, { titel: string; zeilen: WorkplaceRow[] }>()
    for (const z of zeilen) {
      const [k, titel] = gruppe === 'client' ? [z.client?.id ?? '-', z.client?.name ?? 'ohne Kunde'] : [z.project.id, `${z.project.projectNumber} ${z.project.name}`]
      const g = m.get(k) ?? { titel, zeilen: [] }
      g.zeilen.push(z)
      m.set(k, g)
    }
    return [...m.values()]
  }, [zeilen, gruppe])

  async function tu(aktion: () => Promise<unknown>, erfolg: string) {
    setFehler(null)
    setHinweis(null)
    try {
      await aktion()
      setHinweis(erfolg)
      await laden(filterWerte)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  const zumProjekt = (z: WorkplaceRow) => navigate(`/projects/${z.project.id}?tab=billinglist`)

  const zelle = (z: WorkplaceRow, k: string): React.ReactNode => {
    switch (k) {
      case 'client':
        return z.client?.name ?? '—'
      case 'project':
        return (
          <button type="button" className="text-primary hover:underline" onClick={() => zumProjekt(z)}>
            {z.project.projectNumber}
            <span className="block text-xs text-muted-foreground">{z.project.name}</span>
          </button>
        )
      case 'position':
        return z.position ? `${z.position.number} ${z.position.name}` : '—'
      case 'what':
        if (z.kind === 'LINE') return <span className="font-medium">{z.name}</span>
        if (z.kind === 'REGIE')
          return (
            <span>
              Regiestunden{' '}
              <button type="button" className="ml-1 text-primary hover:underline" onClick={() => setRegie(z.position!.id)}>
                Vorschlag prüfen
              </button>
            </span>
          )
        if (z.kind === 'PARTIAL')
          return (
            <span>
              Teilrechnung nach Leistungsstand{' '}
              <button type="button" className="ml-1 text-primary hover:underline" onClick={() => zumProjekt(z)}>
                öffnen
              </button>
            </span>
          )
        if (z.kind === 'RATE')
          return (
            <span>
              Rate „{z.label}"
              {belegwesen && (
                <button
                  type="button"
                  className="ml-2 text-primary hover:underline"
                  onClick={() =>
                    void tu(async () => {
                      const r = await invoicePaymentPlanItem(z.id)
                      navigate(`/correspondence/${r.documentId}`)
                    }, 'Rate als Entwurf angelegt.')
                  }
                >
                  abrechnen
                </button>
              )}
            </span>
          )
        return reiter === 'done' ? 'Abrechnung fertig — noch nicht abgerechnet' : `${z.openLines ?? 0} offene Zeilen${z.regieHours ? ` · ${zahl(z.regieHours)} h Regie` : ''}`
      case 'quantity':
        if (z.kind === 'LINE') return `${zahl(z.quantity)} ${z.unitCode ?? ''}`
        if (z.kind === 'REGIE') return `${zahl(z.hours)} h / ${z.days} Tage`
        if (z.kind === 'RATE') return z.percent != null ? `${zahl(z.percent)} %` : '—'
        return ''
      case 'state':
        if (z.kind === 'LINE' && z.state)
          return (
            <>
              {SALES_LINE_STATE_LABELS[z.state]}
              {z.deliveryNote && <span className="block text-xs text-muted-foreground">{z.deliveryNote.number}</span>}
              {z.inDrafts && z.inDrafts.length > 0 && <span className="block text-xs text-muted-foreground">im Entwurf {z.inDrafts.join(', ')}</span>}
            </>
          )
        if (z.kind === 'PARTIAL') return `${z.progressPercent} % erbracht / ${z.billedPercent} % abgerechnet`
        if (z.kind === 'REGIE') return z.since ? `seit ${alsTag(z.since)}` : ''
        if (z.kind === 'RATE') return z.due ? 'fällig' : 'von Hand'
        return z.billingDoneAt ? `fertig seit ${alsTag(z.billingDoneAt)}` : (z.status ?? '')
      case 'value':
        if (z.kind === 'LINE') return geld(z.lineTotal)
        if (z.kind === 'RATE') return geld(z.amount)
        return ''
      case 'due':
        return z.kind === 'PROJECT' ? (z.dueAt ? alsTag(z.dueAt) : 'nach Fertigstellung') : ''
      case 'scheme':
        return z.scheme ? (SCHEMA[z.scheme] ?? z.scheme) : ''
      default:
        return null
    }
  }

  const spaltenAendern = (keys: string[]) => {
    setSpalten(keys)
    saveUiSetting(COLUMN_LAYOUT_KEY, { columnKeys: keys, columnWidths: {} })
  }
  const markierbar = reiter === 'deliverable' || reiter === 'billable'

  return (
    <div className={pageWide}>
      <div>
        <h1 className="text-3xl font-bold text-foreground">Abrechnung</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Was über alle {terms.projectPlural} abzurechnen ist. Lieferscheine und Rechnungen entstehen hier als Entwurf je Kunde.
        </p>
      </div>
      {darfExport && (
        <div className="flex justify-end">
          <ExportButton
            request={(format, orientation) =>
              workplaceExportRequest(reiter, { ...alsFilter(filterWerte), format, orientation }, gezeigt.map((c) => ({ key: c.key, label: c.label })))
            }
            mail={{
              source: 'EXPORT_PROJECT_BILLING',
              title: t('mailExport.titles.EXPORT_PROJECT_BILLING'),
              subject: `${t('mailExport.titles.EXPORT_PROJECT_BILLING')} – ${REITER.find(([k]) => k === reiter)?.[1] ?? ''}`,
            }}
          />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1 border-b border-border">
        {REITER.map(([k, l, titel]) => (
          <button
            key={k}
            type="button"
            title={titel}
            onClick={() => setReiter(k)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm ${reiter === k ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            {l}
          </button>
        ))}
        <label className="ml-auto flex items-center gap-2 pb-1 text-xs text-muted-foreground">
          gruppiert nach
          <select className={`${schmal} w-36`} value={gruppe} onChange={(e) => setGruppe(e.target.value as 'client' | 'project')}>
            <option value="client">Kunde</option>
            <option value="project">{terms.project}</option>
          </select>
        </label>
      </div>
      <p className="text-xs text-muted-foreground">{REITER.find((r) => r[0] === reiter)?.[2]}</p>

      <FilterBar
        availableFields={felder}
        value={filterWerte}
        onChange={(w) => {
          setFilterWerte(w)
          if (timer.current) clearTimeout(timer.current)
          timer.current = setTimeout(() => void laden(w), 400)
        }}
        layoutKey="project-billing"
        defaultFieldKeys={['q', 'projectSearch', 'positionSearch', 'clientName', 'projectLead', 'scheme']}
      />

      {auswahl.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
          <span className="text-muted-foreground">{auswahl.length} markiert</span>
          <button type="button" className="rounded border border-border px-2 py-1 hover:bg-muted" onClick={() => void tu(() => copySalesLines(auswahl), 'In die Zwischenablage kopiert.')}>
            Kopieren
          </button>
          {belegwesen && (
            <>
              {reiter === 'deliverable' && (
                <button type="button" className="ml-auto rounded-md border border-border px-3 py-1 hover:bg-muted" onClick={() => setErzeugen('DELIVERY_NOTE')}>
                  Lieferschein erzeugen …
                </button>
              )}
              <button type="button" className={`${reiter === 'deliverable' ? '' : 'ml-auto '}rounded-md bg-primary px-3 py-1 font-medium text-primary-foreground`} onClick={() => setErzeugen('INVOICE')}>
                Rechnung erzeugen …
              </button>
            </>
          )}
        </div>
      )}

      {fehler && <p className="text-sm text-destructive">{fehler}</p>}
      {hinweis && <p className="text-sm text-green-700 dark:text-green-400">{hinweis}</p>}

      <div className="relative overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <button
          type="button"
          onClick={() => setKonfig((v) => !v)}
          className={`absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md ${konfig ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
          title="Spalten konfigurieren"
        >
          {konfig ? <Check size={14} /> : <Settings size={14} />}
        </button>
        <HelpPopover topic="columns" className="absolute right-8 top-1.5" />
        {konfig && (
          <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/30 px-3 py-2 pr-16 text-xs">
            <LayoutDefaultActions
              settingKey={COLUMN_LAYOUT_KEY}
              currentValue={{ columnKeys: spalten, columnWidths: {} }}
              onReset={(v) => {
                const keys = (v as { columnKeys?: string[] } | null)?.columnKeys ?? STANDARD
                markUiSettingApplied(COLUMN_LAYOUT_KEY, { columnKeys: keys, columnWidths: {} })
                setSpalten(keys)
              }}
            />
            {hinzu.length > 0 && (
              <select
                className="rounded border border-border bg-background px-1 py-0.5"
                defaultValue=""
                onChange={(e) => {
                  if (e.target.value) spaltenAendern([...spalten, e.target.value])
                  e.target.value = ''
                }}
              >
                <option value="">+ Spalte</option>
                {hinzu.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <th className="w-8 px-2" />
                {gezeigt.map((c) => (
                  <th key={c.key} className={`px-3 py-1.5 font-medium ${c.rechts ? 'text-right' : 'text-left'}`}>
                    {c.label}
                    {konfig && (
                      <button type="button" className="ml-1 text-muted-foreground hover:text-destructive" onClick={() => spaltenAendern(spalten.filter((k) => k !== c.key))}>
                        ✕
                      </button>
                    )}
                  </th>
                ))}
                {/* Platz für Zahnrad und Hilfe über der letzten Spalte. */}
                <th className="w-14" />
              </tr>
            </thead>
            <tbody>
              {!laedt && zeilen.length === 0 && (
                <tr>
                  <td colSpan={gezeigt.length + 2} className="px-4 py-8 text-center text-muted-foreground">
                    Nichts in diesem Reiter.
                  </td>
                </tr>
              )}
              {gruppen.map((g) => (
                <Fragment key={g.titel}>
                  <tr className="border-b border-border bg-muted/30">
                    <td className="px-2 py-1">
                      {markierbar && (
                        <input
                          type="checkbox"
                          aria-label={`${g.titel} markieren`}
                          checked={g.zeilen.filter((z) => z.kind === 'LINE').every((z) => markiert.has(z.id))}
                          onChange={(e) =>
                            setMarkiert((m) => {
                              const n = new Set(m)
                              for (const z of g.zeilen.filter((x) => x.kind === 'LINE')) {
                                if (e.target.checked) n.add(z.id)
                                else n.delete(z.id)
                              }
                              return n
                            })
                          }
                        />
                      )}
                    </td>
                    <td colSpan={gezeigt.length + 1} className="px-3 py-1 text-xs font-medium">
                      {g.titel} · {g.zeilen.length}
                    </td>
                  </tr>
                  {g.zeilen.map((z) => (
                    <tr key={`${z.kind}:${z.id}`} className="border-b border-border last:border-0 hover:bg-accent/30">
                      <td className="px-2 py-1.5">
                        {markierbar && z.kind === 'LINE' && (
                          <input
                            type="checkbox"
                            aria-label={`${z.name} markieren`}
                            checked={markiert.has(z.id)}
                            onChange={() =>
                              setMarkiert((m) => {
                                const n = new Set(m)
                                if (n.has(z.id)) n.delete(z.id)
                                else n.add(z.id)
                                return n
                              })
                            }
                          />
                        )}
                      </td>
                      {gezeigt.map((c) => (
                        <td key={c.key} className={`px-3 py-1.5 ${c.rechts ? 'text-right tabular-nums' : ''}`}>
                          {zelle(z, c.key)}
                        </td>
                      ))}
                      <td />
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {erzeugen && (
        <BelegDialog
          kind={erzeugen}
          lineIds={auswahl}
          preise={preise}
          onClose={() => setErzeugen(null)}
          onDone={(r) => {
            setErzeugen(null)
            if (r.created.length === 1) navigate(`/correspondence/${r.created[0].documentId}`)
            else void tu(async () => undefined, `${r.created.length} Entwürfe angelegt.`)
          }}
        />
      )}
      {regie && (
        <RegieProposalDialog
          positionId={regie}
          scheme="TIME_AND_MATERIAL"
          onClose={() => setRegie(null)}
          onDone={async () => {
            setRegie(null)
            setHinweis('Regievorschlag übernommen — die Zeilen stehen jetzt unter „Lieferbar".')
            await laden(filterWerte)
          }}
        />
      )}
    </div>
  )
}
